import UserDropdown from "components/Dropdowns/UserDropdown.js";
import { useAuth } from "utils/AuthContext.js";
import { capitalizeWords } from "utils/text.js";
import { useHistory } from "react-router-dom";
import { Link, useLocation } from "react-router-dom";
import { AppBar, Box, Button, Collapse, Container, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import { Menu, X } from "lucide-react";
import { useMemo, useState } from "react";
import enarmApexLogoBlanco from "assets/img/enarm-apex-logo-blanco2.png";

export default function Navbar() {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = useMemo(
    () => [
      { label: "Simulador", to: "/admin/examenes" },
      { label: "Practica", to: "/admin/practica" },
      { label: "Flashcards", to: "/admin/flashcards" },
      { label: "Plan de estudio", to: "/admin/plan-estudio" },
      { label: "Actividad", to: "/admin/actividad" },
      { label: "Desempeño", to: "/admin/especialidades" },
      { label: "Estadisticas", to: "/admin/estadisticas" },
    ],
    []
  );

  const handleGoHome = () => {
    history.push("/admin/dashboard");
    setMobileOpen(false);
  };

  const handleCloseMobile = () => {
    setMobileOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: "#312e81",
        borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 } }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => setMobileOpen((prev) => !prev)}
              disableRipple
              disableFocusRipple
              sx={{
                display: { xs: "inline-flex", md: "none" },
                color: "#fff",
                outline: "none",
                boxShadow: "none",
                '&:hover': {
                  backgroundColor: "transparent",
                },
                '&:active': {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
                '&:focus': {
                  outline: "none",
                },
                '&.Mui-focusVisible': {
                  outline: "none",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                },
              }}
              aria-label="Abrir menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </IconButton>

            <Button
              onClick={handleGoHome}
              disableRipple
              disableFocusRipple
              sx={{
                textTransform: "none",
                color: "#fff",
                fontWeight: 200,
                letterSpacing: "0.02em",
                fontSize: { xs: "1.02rem", sm: "1.04rem", md: "1.04rem" },
                px: 0,
                minWidth: "auto",
                outline: "none",
                boxShadow: "none",
                '&:hover': {
                  backgroundColor: "transparent",
                },
                '&:active': {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
                '&:focus': {
                  outline: "none",
                },
                '&.Mui-focusVisible': {
                  outline: "none",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                },
              }}
            >
              <Box
                component="img"
                src={enarmApexLogoBlanco}
                alt="ENARM APEX"
                sx={{
                  height: { xs: 32, sm: 34, md: 36 },
                  width: "auto",
                  display: "block",
                }}
              />
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              flex: 1,
              ml: { md: 1, lg: 2 },
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
            }}
          >
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.to);
              return (
                <Button
                  key={item.to}
                  component={Link}
                  to={item.to}
                  sx={{
                    textTransform: "none",
                    color: isActive ? "#ffffff" : "rgba(226, 232, 240, 0.88)",
                    bgcolor: isActive ? "rgba(99, 102, 241, 0.28)" : "transparent",
                    border: isActive ? "1px solid rgba(129, 140, 248, 0.6)" : "1px solid transparent",
                    borderRadius: "10px",
                    px: { md: 1.1, lg: 1.5 },
                    py: { md: 0.62, lg: 0.75 },
                    fontSize: { md: "0.82rem", lg: "0.9rem" },
                    fontWeight: 300,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: "rgba(99, 102, 241, 0.2)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minHeight: 48 }}>
            <Typography sx={{ display: { xs: "none", lg: "inline-flex" }, alignItems: "center", color: "#fff", fontSize: { lg: "0.94rem" }, fontWeight: 300, lineHeight: 1 }}>
              Hola, {capitalizeWords(user?.user_metadata?.full_name || "Invitado", true)}
            </Typography>
            <UserDropdown />
          </Stack>
        </Toolbar>

        <Collapse
          in={mobileOpen}
          timeout={220}
          unmountOnExit
          sx={{
            display: { xs: "block", md: "none" },
            mx: -2,
            width: "calc(100% + 32px)",
            '& .MuiCollapse-wrapper': {
              width: "100%",
            },
            '& .MuiCollapse-wrapperInner': {
              width: "100%",
            },
          }}
        >
          <Box
            sx={{
              pb: 1,
              px: 0,
              pt: 0.25,
              width: "100%",
              backgroundColor: "#fafbff",
              borderTop: "1px solid rgba(109, 40, 217, 0.2)",
              borderRadius: 0,
            }}
          >
            <Stack spacing={1}>
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.to);

                return (
                  <Button
                    key={item.to}
                    component={Link}
                    to={item.to}
                    onClick={handleCloseMobile}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      color: isActive ? "#312e81" : "rgba(49, 46, 129, 0.88)",
                      bgcolor: "transparent",
                      border: "none",
                      borderRadius: 0,
                      px: 1.5,
                      py: 0.9,
                      fontSize: { xs: "1rem", sm: "1.02rem" },
                      fontWeight: isActive ? 400 : 300,
                      textDecoration: isActive ? "underline" : "none",
                      textUnderlineOffset: "0.32em",
                      textDecorationThickness: "1px",
                      transition: "background-color 180ms ease, color 180ms ease",
                      '&:hover': {
                        bgcolor: "rgba(139, 92, 246, 0.08)",
                      },
                      '&:active': {
                        color: "#1e1b4b",
                        bgcolor: "rgba(61, 24, 121, 0.12)",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        </Collapse>
      </Container>
    </AppBar>
  );
}
